import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Avatar } from "../components/ui";
import { markdownToPlain } from "../components/RichText";
import { createDashboardViewModel } from "../lib/dashboard";
import { isObserverUser } from "../lib/domain";
import type { ActionItem, LexData, RecommendedItem } from "../lib/types";

type ActivityTab = "my" | "network" | "gov";

export function DashboardPage({ data }: { data: LexData; mode?: "convex" | "demo" }) {
  const vm = useMemo(() => createDashboardViewModel(data), [data]);
  const observer = isObserverUser(data.viewer);
  const [activityTab, setActivityTab] = useState<ActivityTab>("network");

  const firstName = data.viewer.name.split(" ")[0];
  const summary = vm.action_summary;
  const pulse = vm.governance_pulse;
  const inDrafting = data.discussions.filter((discussion) => discussion.status === "drafting").length;
  const activeFiles = data.legislationFiles.length;

  const activity =
    activityTab === "my" ? vm.activity_feeds.my_activity_items : activityTab === "gov" ? vm.activity_feeds.governance_activity_items : vm.activity_feeds.network_activity_items;
  const pinnedPosts = data.discussions.filter((discussion) => discussion.pinned).sort((a, b) => b.lastActivityAt - a.lastActivityAt);

  return (
    <>
      <div className="phead">
        <div className="eyebrow">Dashboard</div>
        <h1 className="h1 serif">Welcome back, {firstName}</h1>
        <p className="lead">
          {summary.urgent_count > 0
            ? `${spell(summary.urgent_count)} ${summary.urgent_count === 1 ? "item needs" : "items need"} your attention today`
            : "You're all caught up"}{" "}
          — alongside member requests and the network's active files.
        </p>
      </div>

      {pinnedPosts.map((post) => (
        <div key={post.id} className="sec card" style={{ borderLeft: "3px solid var(--accent)" }}>
          <div style={{ padding: "16px 20px" }}>
            <div className="eyebrow" style={{ marginBottom: 6 }}>📌 Pinned · From the network</div>
            <div className="row-title" style={{ fontSize: 18 }}>{post.title}</div>
            <p className="lead" style={{ marginTop: 6, maxWidth: "none", whiteSpace: "pre-wrap" }}>{pinnedPreview(post.body)}</p>
            <Link className="btn-sm btn-primary" style={{ height: 30, marginTop: 10, display: "inline-flex", alignItems: "center" }} to={`/discussions?discussion=${post.id}`}>Read the welcome</Link>
          </div>
        </div>
      ))}

      <div className="statgrid">
        <div className="stat stat-urgent">
          <div className="stat-num">{summary.urgent_count}</div>
          <div className="stat-lbl">Need attention</div>
          <div className="stat-sub">Due within 48 hours</div>
        </div>
        <Stat num={summary.open_votes_count} label="Open vote" sub={pulse.closing_soon_count > 0 ? "Closing soon" : "Across the network"} />
        <Stat num={summary.cooperation_requests_count} label="Exchange requests" sub="To your firm" />
        <Stat num={summary.drafts_count} label="Drafts" sub="In your workspace" />
        <Stat num={activeFiles} label="Active files" sub="Across the network" />
      </div>

      <div className="dgrid">
        <div>
          <div className="sec card">
            <div className="cardhd">
              <span className="cardtitle">Needs your attention</span>
              {observer ? null : <Link className="link" to="/my-workspace?view=actions">View all</Link>}
            </div>
            {vm.urgent_actions.length > 0 ? (
              vm.urgent_actions.map((item) => <AttentionRow key={item.id} item={item} />)
            ) : (
              <div className="rec"><div className="row-meta" style={{ margin: 0 }}>Nothing urgent right now.</div></div>
            )}
          </div>

          <div className="sec card">
            <div className="cardhd">
              <span className="cardtitle">Recommended for you</span>
              <span className="row-meta" style={{ margin: 0 }}>Based on your practice areas</span>
            </div>
            {vm.recommended_items.length > 0 ? (
              vm.recommended_items.map((item) => <RecommendedRow key={item.id} item={item} />)
            ) : (
              <div className="rec"><div className="row-meta" style={{ margin: 0 }}>No recommendations yet.</div></div>
            )}
          </div>
        </div>

        <div>
          {observer ? null : (
            <div className="sec card">
              <div className="cardhd">
                <span className="cardtitle">Governance pulse</span>
                {pulse.closing_soon_count > 0 ? <span className="pill pill-warn">{pulse.closing_soon_count} closing soon</span> : null}
              </div>
              <div className="gp">
                <PulseCell num={pulse.adopted_decisions_count} label="Adopted" />
                <PulseCell num={pulse.rejected_decisions_count} label="Rejected" />
                <PulseCell num={pulse.no_quorum_count} label="No quorum" />
                <PulseCell num={pulse.open_votes_count} label="Open vote" />
                <PulseCell num={inDrafting} label="In drafting" />
                <PulseCell num={data.users.length} label="Members" />
              </div>
            </div>
          )}

          <div className="sec card">
            <div className="tabs">
              <button type="button" className={`tab${activityTab === "my" ? " tab-on" : ""}`} onClick={() => setActivityTab("my")}>My activity</button>
              <button type="button" className={`tab${activityTab === "network" ? " tab-on" : ""}`} onClick={() => setActivityTab("network")}>Network</button>
              {observer ? null : <button type="button" className={`tab${activityTab === "gov" ? " tab-on" : ""}`} onClick={() => setActivityTab("gov")}>Governance</button>}
            </div>
            <div className="feed">
              {activity.length > 0 ? (
                activity.slice(0, 6).map((item) => (
                  <Link key={item.id} to={item.action_href ?? "/discussions"} className="feeditem" style={{ textDecoration: "none" }}>
                    <Avatar name={item.actor || "LexMosaic"} imageUrl={item.actorAvatarUrl} size="sm" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="feed-title">{item.title}</div>
                      <div className="feed-meta">{item.actor} · {item.description}</div>
                    </div>
                    <span className="feed-meta" style={{ whiteSpace: "nowrap" }}>{relativeAge(item.occurred_at)}</span>
                  </Link>
                ))
              ) : (
                <div className="feeditem"><div className="feed-meta">No recent activity.</div></div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Stat({ num, label, sub }: { num: number; label: string; sub: string }) {
  return (
    <div className="stat">
      <div className="stat-num">{num}</div>
      <div className="stat-lbl">{label}</div>
      <div className="stat-sub">{sub}</div>
    </div>
  );
}

function PulseCell({ num, label }: { num: number; label: string }) {
  return (
    <div className="gpcell">
      <div className="gp-num">{num}</div>
      <div className="gp-lbl">{label}</div>
    </div>
  );
}

function AttentionRow({ item }: { item: ActionItem }) {
  const tone = actionTone(item.type);
  return (
    <div className="attn">
      <span className={`attn-mark${tone.mark === "amber" ? " amber" : ""}`} style={tone.mark === "sky" ? { background: "var(--sky)" } : undefined} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 5 }}>
          <span className={`pill ${tone.pill}`}>{item.type}</span>
          <span className="row-meta" style={{ margin: 0 }}>{dueLabel(item)}</span>
        </div>
        <div className="row-title">{item.title}</div>
        <div className="row-meta">{item.module} · {item.status}</div>
      </div>
      <Link className={`btn-sm${tone.primary ? " btn-primary" : ""}`} to={item.action_href ?? "/my-workspace?view=actions"}>{item.action_label}</Link>
    </div>
  );
}

function RecommendedRow({ item }: { item: RecommendedItem }) {
  const stats = item.replies_count > 0 ? `${item.replies_count} ${item.replies_count === 1 ? "reply" : "replies"} · ${item.contributors_count} contributors` : `${item.contributors_count} contributors`;
  const primaryLabel = item.primary_action.label;
  return (
    <div className="rec">
      <div className="reason">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l2.6 6.6L22 9.3l-5 4.6L18.2 21 12 17.3 5.8 21 7 13.9 2 9.3l7.4-.7z" /></svg>
        {item.reason_shown}
      </div>
      <div className="row-title">{item.title}</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 9 }}>
        <span className={`pill ${pillForPostType(item.post_type)}`}>{item.post_type}</span>
        {item.topics.slice(0, 2).map((topic) => <span key={topic} className="tag">{topic}</span>)}
      </div>
      <div className="recmeta">
        <span>{item.author}</span>
        <span>{item.age}</span>
        <span>{stats}</span>
        <Link className={`btn-sm${primaryLabel === "Vote now" ? " btn-primary" : ""}`} style={{ marginLeft: "auto", height: 30 }} to={item.primary_action.href ?? "/discussions"}>{primaryLabel}</Link>
      </div>
    </div>
  );
}

function actionTone(type: string): { pill: string; mark: "red" | "amber" | "sky"; primary: boolean } {
  if (type.toLowerCase().includes("vote")) return { pill: "pill-bad", mark: "red", primary: true };
  if (type.toLowerCase().includes("conflict")) return { pill: "pill-warn", mark: "amber", primary: false };
  if (type.toLowerCase().includes("review") || type.toLowerCase().includes("view")) return { pill: "pill-sky", mark: "sky", primary: false };
  return { pill: "pill-warn", mark: "amber", primary: false };
}

function pillForPostType(postType: string) {
  const normalized = postType.toLowerCase();
  if (normalized === "vote") return "pill-warn";
  if (normalized === "question") return "pill-sky";
  if (normalized === "resource") return "pill-neutral";
  if (normalized === "proposal") return "pill-warn";
  return "pill-neutral";
}

function dueLabel(item: ActionItem) {
  if (item.due_at) return `Due ${relativeAge(item.due_at)}`;
  if (item.action_label) return item.status;
  return item.status;
}

function relativeAge(time: number) {
  const delta = time - Date.now();
  const abs = Math.abs(delta);
  const hour = 60 * 60 * 1000;
  const dayMs = 24 * hour;
  if (abs < hour) return delta >= 0 ? "within the hour" : "just now";
  if (abs < dayMs) {
    const hours = Math.max(1, Math.round(abs / hour));
    return delta >= 0 ? `in ${hours}h` : `${hours}h ago`;
  }
  const days = Math.max(1, Math.round(abs / dayMs));
  return delta >= 0 ? `in ${days}d` : `${days}d ago`;
}

function spell(count: number) {
  const words = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];
  return words[count] ?? String(count);
}

function pinnedPreview(body: string) {
  const firstParagraph = markdownToPlain(body.split(/\n\s*\n/)[0]?.trim() ?? body.trim());
  if (firstParagraph.length <= 320) return firstParagraph;
  return `${firstParagraph.slice(0, 320).trimEnd()}…`;
}
